/**
 * ApplyRuleToHistoryDialog
 *
 * Opens when the user clicks an existing rule chip in CategoryRulesModal.
 * Shows all bank transactions that match the rule text but are NOT yet
 * classified under this rule's category — including:
 *   • Truly unclassified rows (accounting_category_id IS NULL)
 *   • Rows classified under a *different* category (shown with ⚠ warning)
 *
 * Flow
 * ────
 *  1. Parent opens dialog with a pre-loaded list of matching transactions.
 *  2. Every row is checked by default (opt-out model).
 *  3. User can uncheck specific rows to exclude them.
 *  4. "Confirm & Update" bulk-updates only the selected rows.
 *  5. Dialog closes; parent receives `onApplied(updatedCount)` callback.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  X,
  CheckSquare,
  Square,
  Loader2,
  CheckCircle2,
  AlertCircle,
  CalendarDays,
  Tag,
  TriangleAlert,
} from 'lucide-react';
import { BankTransaction } from '../../types/database';
import { bankCategoryRulesService } from '../../lib/bankCategoryRulesService';
import { formatDateSafe } from '../../lib/dateUtils';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ApplyRuleToHistoryDialogProps {
  /** Controls visibility */
  isOpen: boolean;
  /** Called when the dialog is dismissed (X button or Cancel) */
  onClose: () => void;
  /**
   * Called after a successful bulk update.
   * `updatedCount` is the number of rows actually written.
   */
  onApplied: (updatedCount: number) => void;

  /** The category ID to stamp onto the matched transactions */
  categoryId: string;
  /** Display name of the category — used in the header */
  categoryName: string;
  /** The exact description string this rule watches */
  exactDescription: string;
  /** Pre-loaded matching transactions (fetched before the dialog opens) */
  transactions: BankTransaction[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatAmount(n: number): string {
  return new Intl.NumberFormat('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(n));
}

// ─── Sub-component: single transaction row ────────────────────────────────────

interface TxRowProps {
  tx: BankTransaction;
  checked: boolean;
  onToggle: (id: string) => void;
}

function TxRow({ tx, checked, onToggle }: TxRowProps) {
  const isCredit   = tx.amount >= 0;
  // If accounting_categories is populated, this tx already has a *different*
  // category assigned — the user is about to override it.
  const existingCategory = tx.accounting_categories?.name ?? null;

  return (
    <label
      htmlFor={`tx-${tx.id}`}
      className={`
        flex flex-col gap-0.5 px-4 py-2 cursor-pointer select-none
        border-b border-gray-100 last:border-0 transition-colors
        ${checked ? 'bg-white hover:bg-indigo-50' : 'bg-gray-50 hover:bg-gray-100 opacity-60'}
        ${existingCategory ? 'border-r-2 border-amber-300' : ''}
      `}
    >
      {/* ── Main row ───────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        {/* Hidden real checkbox + custom visual */}
        <input
          id={`tx-${tx.id}`}
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(tx.id)}
          className="sr-only"
        />
        <span className="flex-shrink-0 text-indigo-600">
          {checked
            ? <CheckSquare className="h-4 w-4" />
            : <Square className="h-4 w-4 text-gray-400" />}
        </span>

        {/* Date */}
        <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-gray-500 w-[78px]">
          <CalendarDays className="h-3 w-3 flex-shrink-0" />
          {formatDateSafe(tx.transaction_date)}
        </span>

        {/* Description */}
        <span className="flex-1 text-sm text-gray-800 truncate min-w-0">
          {tx.description}
        </span>

        {/* Bank account badge */}
        {tx.bank_accounts?.nickname && (
          <span className="flex-shrink-0 hidden sm:inline-flex items-center gap-1 text-xs text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 max-w-[110px] truncate">
            <Tag className="h-3 w-3 flex-shrink-0" />
            {tx.bank_accounts.nickname}
          </span>
        )}

        {/* Amount */}
        <span
          className={`flex-shrink-0 text-sm font-semibold tabular-nums w-[88px] text-right ${
            isCredit ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {isCredit ? '+' : '−'}
          {formatAmount(tx.amount)} ₪
        </span>
      </div>

      {/* ── Override warning (only when already classified differently) ── */}
      {existingCategory && (
        <div className="flex items-center gap-1.5 pr-7 text-xs text-amber-700">
          <TriangleAlert className="h-3 w-3 flex-shrink-0 text-amber-500" />
          <span>
            מסווג כרגע כ‑<strong className="font-semibold">{existingCategory}</strong>
            {' '}— האישור ידרוס סיווג זה
          </span>
        </div>
      )}
    </label>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ApplyRuleToHistoryDialog({
  isOpen,
  onClose,
  onApplied,
  categoryId,
  categoryName,
  exactDescription,
  transactions,
}: ApplyRuleToHistoryDialogProps) {
  // ── Local checkbox state ─────────────────────────────────────────────────
  // Start with ALL transactions checked (opt-out model).
  const [checkedIds, setCheckedIds] = useState<Set<string>>(
    () => new Set(transactions.map((t) => t.id)),
  );
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Keep checkedIds in sync if the transactions list ever changes
  // (e.g. parent re-renders).  We only ADD new IDs; we never un-check IDs the
  // user already unchecked.
  // NOTE: intentionally omitted from dep-array — we only want to pre-check
  //       newly-arriving rows, not reset user choices on every render.

  // ── Derived ──────────────────────────────────────────────────────────────
  const selectedCount  = useMemo(() => checkedIds.size, [checkedIds]);
  const allSelected    = selectedCount === transactions.length;
  const noneSelected   = selectedCount === 0;
  // Count how many of the returned transactions already have a different
  // category — used to show the override count in the header.
  const overrideCount  = useMemo(
    () => transactions.filter((t) => t.accounting_categories?.name != null).length,
    [transactions],
  );

  // ── Handlers ─────────────────────────────────────────────────────────────
  const toggleOne = useCallback((id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setCheckedIds(
      allSelected
        ? new Set()
        : new Set(transactions.map((t) => t.id)),
    );
  }, [allSelected, transactions]);

  const handleApply = useCallback(async () => {
    const ids = [...checkedIds];
    if (ids.length === 0) { onClose(); return; }

    setApplying(true);
    setApplyError(null);

    const { data, error } = await bankCategoryRulesService.bulkApplyCategoryRule(
      ids,
      categoryId,
    );

    setApplying(false);

    if (error) {
      setApplyError(error.message ?? 'שגיאה בעדכון הסיווגים');
      return;
    }

    onApplied(data?.updatedCount ?? ids.length);
    onClose();
  }, [checkedIds, categoryId, onApplied, onClose]);

  // ── Early return ─────────────────────────────────────────────────────────
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      dir="rtl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="arh-title"
    >
      {/* ── Dialog panel ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl flex flex-col max-h-[85vh]">

        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 id="arh-title" className="text-base font-semibold text-gray-900 leading-tight">
              נמצאו {transactions.length} עסקאות תואמות לכלל
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              האם לסווג אותן כ‑<strong className="text-gray-700">{categoryName}</strong>?
              {overrideCount > 0 && (
                <span className="mr-1.5 inline-flex items-center gap-1 text-amber-600 font-medium">
                  <TriangleAlert className="h-3 w-3" />
                  {overrideCount} כבר מסווגות אחרת — הסיווג ידרס
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={applying}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0 mt-0.5 disabled:opacity-40"
            aria-label="סגור"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Description chip ─────────────────────────────────────────── */}
        <div className="px-5 py-2.5 border-b border-gray-100 flex-shrink-0 flex items-center gap-2 bg-gray-50">
          <span className="text-xs text-gray-500">ניסוח הכלל:</span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-xs font-medium max-w-full truncate">
            {exactDescription}
          </span>
        </div>

        {/* ── Select-all toolbar ───────────────────────────────────────── */}
        <div className="px-4 py-2 border-b border-gray-100 flex-shrink-0 flex items-center justify-between bg-white">
          <button
            type="button"
            onClick={toggleAll}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600
              hover:text-indigo-700 transition-colors"
          >
            {allSelected
              ? <CheckSquare className="h-4 w-4 text-indigo-600" />
              : <Square className="h-4 w-4" />}
            {allSelected ? 'בטל בחירת הכל' : 'בחר הכל'}
          </button>
          <span className="text-xs text-gray-400">
            {selectedCount} מתוך {transactions.length} נבחרות
          </span>
        </div>

        {/* ── Scrollable transaction list ───────────────────────────────── */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {transactions.map((tx) => (
            <TxRow
              key={tx.id}
              tx={tx}
              checked={checkedIds.has(tx.id)}
              onToggle={toggleOne}
            />
          ))}
        </div>

        {/* ── Error banner ─────────────────────────────────────────────── */}
        {applyError && (
          <div className="mx-5 mt-3 flex items-center gap-2 p-2.5 bg-red-50 border border-red-200
            rounded-md text-xs text-red-700 flex-shrink-0">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            {applyError}
          </div>
        )}

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <div className="px-5 py-3.5 border-t border-gray-200 flex items-center justify-between flex-shrink-0 gap-3">
          {/* Cancel */}
          <button
            type="button"
            onClick={onClose}
            disabled={applying}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300
              rounded-md hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            ביטול
          </button>

          {/* Confirm & Update */}
          <button
            type="button"
            onClick={() => void handleApply()}
            disabled={applying || noneSelected}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white
              bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50
              disabled:cursor-not-allowed transition-colors"
          >
            {applying
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <CheckCircle2 className="h-3.5 w-3.5" />}
            {applying
              ? 'מעדכן…'
              : `אשר ועדכן (${selectedCount})`}
          </button>
        </div>
      </div>
    </div>
  );
}
